import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Leaf, ArrowLeft, Shield, Database, Lock, Eye, Clock, Globe, Mail } from "lucide-react";

export default function Privacy() {
  const sections = [
    {
      icon: Database,
      title: "1. Données Collectées",
      content: `VF Decide collecte les catégories de données suivantes :

**Données d'identification :**
- Nom, prénom, adresse email
- Nom de l'organisation
- Identifiants de connexion (hashés)

**Données d'utilisation :**
- Décisions saisies et analyses générées
- Objectifs RSE définis
- Historique de navigation dans l'application
- Logs de connexion et adresses IP

**Données de paiement :**
- Traitées exclusivement par Stripe (nous ne stockons aucune donnée bancaire)
- Identifiant client Stripe pour la gestion des abonnements`
    },
    {
      icon: Eye,
      title: "2. Finalités du Traitement",
      content: `Vos données sont traitées pour les finalités suivantes :

- **Fourniture du service** : Analyse IA de vos décisions, génération de scores ESG, suivi d'objectifs
- **Gestion du compte** : Authentification, personnalisation, support client
- **Amélioration du service** : Statistiques anonymisées, optimisation des algorithmes IA
- **Communication** : Notifications d'alertes, newsletters (avec consentement), informations importantes
- **Obligations légales** : Conservation des données de facturation, réponse aux autorités

Base légale : Exécution du contrat (Art. 6.1.b RGPD), consentement (Art. 6.1.a), intérêt légitime (Art. 6.1.f), obligation légale (Art. 6.1.c).`
    },
    {
      icon: Clock,
      title: "3. Durée de Conservation",
      content: `Les données sont conservées selon les durées suivantes :

- **Données de compte** : Pendant la durée de l'abonnement + 3 ans après la clôture
- **Données de décisions** : Pendant l'abonnement + 1 an (puis anonymisation)
- **Logs de connexion** : 12 mois
- **Données de facturation** : 10 ans (obligation comptable)
- **Données analytiques** : Anonymisées et conservées indéfiniment

À l'expiration de ces délais, les données sont supprimées ou anonymisées de manière irréversible.`
    },
    {
      icon: Globe,
      title: "4. Destinataires et Transferts",
      content: `Vos données peuvent être partagées avec :

**Sous-traitants :**
- **Emergent Labs** (hébergement cloud) - Données hébergées en Union Européenne
- **Stripe** (paiements) - Certifié PCI-DSS, données en UE
- **OpenAI** (analyse IA) - Données traitées conformément à leur DPA, pas de conservation
- **Resend** (emails) - Serveurs en UE

**Tiers autorisés :**
- Autorités légales sur demande judiciaire
- Auditeurs dans le cadre de certifications

Aucun transfert hors UE sans garanties appropriées (Clauses Contractuelles Types ou équivalent).`
    },
    {
      icon: Lock,
      title: "5. Sécurité des Données",
      content: `VF ACTIONS met en œuvre les mesures de sécurité suivantes :

**Mesures techniques :**
- Chiffrement SSL/TLS pour toutes les communications
- Chiffrement des données sensibles au repos (AES-256)
- Authentification forte et hashing bcrypt des mots de passe
- Pare-feu applicatif et protection DDoS
- Sauvegardes quotidiennes chiffrées

**Mesures organisationnelles :**
- Accès aux données limité au personnel habilité
- Formation régulière des équipes à la sécurité
- Audits de sécurité annuels
- Procédures de réponse aux incidents

En cas de violation de données, les personnes concernées et la CNIL seront notifiées dans les 72 heures conformément à l'article 33 du RGPD.`
    },
    {
      icon: Shield,
      title: "6. Vos Droits",
      content: `Conformément au RGPD, vous disposez des droits suivants :

- **Droit d'accès** : Obtenir une copie de vos données personnelles
- **Droit de rectification** : Corriger des données inexactes
- **Droit à l'effacement** : Demander la suppression de vos données
- **Droit à la portabilité** : Récupérer vos données dans un format structuré
- **Droit d'opposition** : Vous opposer au traitement à des fins de prospection
- **Droit de limitation** : Restreindre le traitement dans certains cas
- **Droit de retrait du consentement** : À tout moment, sans effet rétroactif

**Pour exercer vos droits :**
Email : privacy@vf-decide.com
Courrier : VF ACTIONS SAS - DPO, 15 Avenue de la RSE, 75008 Paris

Délai de réponse : 1 mois maximum (prolongeable de 2 mois si complexité).

**Réclamation :** Vous pouvez introduire une réclamation auprès de la CNIL (www.cnil.fr).`
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
          </Link>
          <Link to="/">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            Protection des Données
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
            Politique de Confidentialité
          </h1>
          <p className="text-lg text-muted-foreground">
            Conformité RGPD - Dernière mise à jour : 1er janvier 2026
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="pb-8 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-md bg-primary/5">
            <CardContent className="p-6">
              <p className="text-muted-foreground leading-relaxed">
                VF ACTIONS SAS ("nous", "notre", "VF Decide") s'engage à protéger la vie privée de ses utilisateurs. 
                Cette politique décrit comment nous collectons, utilisons et protégeons vos données personnelles 
                conformément au Règlement Général sur la Protection des Données (RGPD - UE 2016/679).
              </p>
              <div className="mt-4 p-4 bg-background rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Responsable du traitement :</strong> VF ACTIONS SAS<br />
                  <strong className="text-foreground">Adresse :</strong> 15 Avenue de la RSE, 75008 Paris<br />
                  <strong className="text-foreground">DPO :</strong> privacy@vf-decide.com
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {sections.map((section, index) => (
            <Card key={index} className="border-0 shadow-md" data-testid={`privacy-section-${index}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <section.icon className="w-5 h-5 text-primary" />
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-muted-foreground whitespace-pre-line leading-relaxed prose prose-sm max-w-none">
                  {section.content.split('**').map((part, i) => 
                    i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Cookies */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Mail className="w-5 h-5 text-primary" />
                7. Cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-3">
              <p>VF Decide utilise des cookies essentiels au fonctionnement du service :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong className="text-foreground">Authentification</strong> : Maintien de votre session</li>
                <li><strong className="text-foreground">Préférences</strong> : Mémorisation de vos choix d'interface</li>
                <li><strong className="text-foreground">Sécurité</strong> : Protection contre les attaques CSRF</li>
              </ul>
              <p>Aucun cookie de tracking publicitaire n'est utilisé.</p>
            </CardContent>
          </Card>

          {/* Links */}
          <div className="flex flex-wrap justify-center gap-4 pt-8">
            <Link to="/mentions-legales">
              <Button variant="outline" className="rounded-full">
                Mentions Légales
              </Button>
            </Link>
            <Link to="/cgu">
              <Button variant="outline" className="rounded-full">
                Conditions Générales d'Utilisation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-card border-t border-border">
        <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
          <p>© 2026 VF Decide - VF ACTIONS SAS. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
